import React, { useState, useEffect} from "react";
import axios from "axios";
import MainMenu from "./MainMenu.js";
import { TextField, Button, IconButton, InputAdornment, Stack, Box, Typography } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";

import { useLanguage } from '../context/LanguageContext.js';
import { useGroupe } from "../context/GroupeContext.js";

import { logDev } from "../utils/logger.js";

import SnackbarFeedback from "../pages/SnackbarFeedback.js";

// 🧩 Composant principal de connexion
const ConnexionGroupe = ({ setMode= () => {}, onGroupeValide }) => {
  const { setGroupe } = useGroupe();

  // 📝 États pour le nom du groupe et le mot de passe
  const { t, lang } = useLanguage();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [nom, setNom] = useState("");
  const [motdepasse, setMotdepasse] = useState("");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [loading, setLoading] = useState(false);

   // 📐 Gestion des hauteurs dynamiques (header/footer/menu)
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [mainMenuHeight, setMainMenuHeight] = useState(0);

  const topOffset = headerHeight + (isMobile ? footerHeight : mainMenuHeight);
  const bottomOffset = isMobile ? mainMenuHeight : 0;

  // 👁️ État pour afficher ou masquer le mot de passe
  const [showPassword, setShowPassword] = useState(false); 

  // 📏 Hauteur du bottom nav (mobile)
  const [bottomNavHeight, setBottomNavHeight] = useState(0);

  // 📌 Mise à jour des hauteurs (header/footer/menu)
  useEffect(() => {
  const header = document.querySelector('.header');
  const footer = document.querySelector('.footer');
  const menu = document.querySelector('.main-menu'); // ← classe à ajouter dans MainMenu

  const updateHeights = () => {
    if (header) setHeaderHeight(header.offsetHeight);
    if (footer) setFooterHeight(footer.offsetHeight);
    if (menu) setMainMenuHeight(menu.offsetHeight);
  };

  updateHeights();
  window.addEventListener('resize', updateHeights);
  return () => window.removeEventListener('resize', updateHeights);
}, []);

// 📌 Récupération hauteur du bottom navigation (mobile)
useEffect(() => {
  const nav = document.querySelector('.MuiBottomNavigation-root');
  if (nav) {

    const updateHeight = () => setBottomNavHeight(nav.offsetHeight);
    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }
}, []);


useEffect(() => {
  const menu = document.querySelector('.main-menu');
  if (menu) {
    const updateHeight = () => setMainMenuHeight(menu.offsetHeight);
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }
}, []);

 // 🔁 AUTO‑LOGIN si identifiants stockés
useEffect(() => {
  const savedGroup = localStorage.getItem("groupeNom");
  const savedPassword = localStorage.getItem("password");

  if (savedGroup && savedPassword) {
    setNom(savedGroup);
    setMotdepasse(savedPassword);

    // Auto-login direct
    /*handleConnexion(savedGroup, savedPassword);*/
  }
}, []);

 // 🔔 Affichage message après upgrade Stripe
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const upgrade = params.get("upgrade");
  const duration = parseInt(params.get("duration"), 10);

  if (upgrade === "success") {
    let message;
    if (duration) {
      // Phrase construite en JS, traduction pour la partie fixe
      message = `${t("SubscriptionUpdated")} (${duration} mois)`;
      // ou bien directement :
      // message = `Votre forfait ${duration} mois est actif.`;
    } else {
      message = t("SubscriptionUpdated");
    }
    
    setSnackbar({
      open: true,
      severity: "success",
      message,
      persistent: true
    });
  }
}, []);

  // ⚠️ État pour afficher un message d'erreur
  const [erreur, setErreur] = useState("");

  const lancerConnexion = async () => {
    setLoading(true);
    setErreur("");

    try {
      await handleConnexion(); // on appelle TA fonction existante
    } finally {
      setLoading(false);
    }
  };


  // 🔐 Fonction appelée lors du clic sur "Se connecter"
  const handleConnexion = async (nomParam, mdpParam) => {

    // 🧹 Nettoyage + fallback sur les states
    const nomFinal = (nomParam ?? nom).trim();
    const mdpFinal = (mdpParam ?? motdepasse).trim();
  
    // 🟦 MODE LOCAL → on simule la connexion pour éviter CORS Render
    if (window.location.hostname === "localhost") {
      console.log("⏭️ Skip /groupes/connexion en local (CORS Render)");

    // Simulation d'une réponse backend
    const fakeId = "local-test-group";
    const fakeRole = "admin"; // ou "user" si tu veux tester l'autre flow

    setGroupe(fakeId, fakeRole);
    navigate(fakeRole === "admin" ? "/contacts" : "/survey");

    return;
  }

  // 🟥 MODE PROD → appel réel Render
  try {
    logDev("[ConnexionGroupe] tentative connexion", { nom: nomFinal, motdepasse: "•••" });

    const res = await axios.post(
      `${process.env.REACT_APP_API}/groupes/connexion`,
      /*{ nom: nom.trim(), motdepasse: motdepasse.trim() },*/
      { nom: nomFinal, motdepasse: mdpFinal },
      { withCredentials: true }
    );

    const { _id, role } = res.data;

    logDev("[ConnexionGroupe] réponse API", res.data);

    setGroupe(_id, role);
    
    navigate(role === "admin" ? "/contacts" : "/survey");

    logDev("✅ Connexion réussie", { groupeId: _id, role });

    /*localStorage.setItem("groupeId", nom);
    localStorage.setItem("password", motdepasse);*/

    // 💾 Sauvegarde pour auto‑login
    localStorage.setItem("groupeId", _id);
    localStorage.setItem("groupeNom", nomFinal); 
    localStorage.setItem("password", mdpFinal);


  } catch (err) {
    logDev("❌ Erreur connexion groupe", {
      status: err.response?.status,
      message: err.response?.data?.message,
      raw: err
    });

    const msg = err.response?.data?.message || t('IncorrectNameOrPassword');
    setErreur(msg);
  }
};

// 📧 Envoi des mots de passe par email
const handleDemandeMotsDePasse = async () => {
  if (!nom.trim()) {
    setErreur(t('PleaseEnterAGroupName'));
    return;
  }
  try {

    logDev("📤 Envoi demande mots de passe", { nom: nom.trim(), langue: lang });

    const res = await axios.post(`${process.env.REACT_APP_API}/groupes/send-passwords`, 
    { nom: nom.trim(), langue: lang },
      { withCredentials: true }
    );

    if (res.data.success) {
      setSnackbar({
      open: true,
      message: t("PasswordsSentToAdminEmail"),
    severity: "success"
  });
    } else {
      setErreur(t("GroupNotFoundOrNoEmail"));
    }
  } catch (err) {
    logDev("❌ Erreur envoi mots de passe", { message: err.message, stack: err.stack });
    const msg = err.response?.data?.message || t("ServerError");
    setErreur(msg);
  }
};

  // 🖼️ Rendu du formulaire de connexion
  return (
    <>
      <Box
        sx={{
          minHeight: "100vh", // prend toute la hauteur de l'écran
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          paddingX: 2, // marge horizontale sur mobile
          paddingTop: `${topOffset}px`,
          paddingBottom: isMobile ? `${footerHeight}px` : `${mainMenuHeight}px`,
          boxSizing: "border-box",
          backgroundColor: "#f4ece2"
        }}
      >
        <Stack 
        spacing={2} 
        sx={{ 
          maxWidth: 360, 
          width: "100%", 
          marginBottom: 8,
          marginTop: isMobile ? 0 : 8 }}>

            {/* 🔔 Notice rôle : message différent pour admin ou utilisateur */}
            <div
              style={{
                backgroundColor: "#2f2f2fff",
                padding: "12px 16px",
                borderRadius: "6px",
                marginBottom: isMobile ? "12px" : "32px",
                fontSize: "1rem",
                fontWeight: "bold",
                textAlign: "center",
                color: "#fff",
                border: "2px solid #d4af37", // doré
                boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                backgroundImage: "radial-gradient(circle at top left, rgba(255,255,255,0.05), transparent 70%)"
              }}
            >
                {t('GroupConnection')}
            </div>
      
          {/* Champ pour le nom du groupe */}
          <TextField
            label={t('GroupName')}
            variant="outlined"
            fullWidth
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            size="small"
            inputProps={{
              autoCapitalize: "none", // ✅ empêche la majuscule automatique
              inputMode: "text",      // ✅ clavier texte standard
            }}
            sx={{
              backgroundColor: "#fff",   // 👈 fond blanc
              borderRadius: 1            // 👈 arrondi léger (optionnel)
            }}
          />

          <TextField
            label={t('Password')}
            variant="outlined"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={motdepasse}
            onChange={(e) => setMotdepasse(e.target.value)}
            size="small"
            inputProps={{
              autoCapitalize: "none", // ✅ empêche la majuscule automatique
              inputMode: "text",      // ✅ clavier texte standard
            }}
            sx={{
              backgroundColor: "#fff",   // 👈 fond blanc
              borderRadius: 1            // 👈 arrondi léger (optionnel)
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
      
          {/* Bouton de connexion */}
          <Button
            variant="contained"
            /*onClick={() => handleConnexion()}*/
            onClick={lancerConnexion}
            fullWidth
            sx=
            {{
            color: "#fff",    
            backgroundColor: "#3399FF",          // texte
            borderColor: "#3399FF",        // bordure
            '&:hover': {
            backgroundColor: "#3399FF",  // fond au survol
            color: "#fff",               // texte au survol
            }, 
              fontWeight: "bold", 
              fontSize: "1rem", 
              mt: 2 }}
          >
            {t('LogIn')}
          </Button>

          {loading && (
            <Typography
              sx={{
                mt: 1,
                fontSize: "0.9rem",
                color: "#555",
                textAlign: "center",
                fontStyle: "italic",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px"
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  animation: "rotateHourglass 1.2s linear infinite",
                  fontSize: "1.2rem"
                }}
              >
                ⏳
              </span>

              {t("PleaseWaitWakeUp")}
            </Typography>
          )}



          {/* Message d'erreur si la connexion échoue */}
          {erreur && <p style={{ color: "red" }}>{erreur}</p>}
        </Stack>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleDemandeMotsDePasse}
          sx={{
            mt: 1,
            borderColor: "#d4af37",
            color: "#d4af37",
            fontWeight: "bold",
            fontSize: "0.95rem",
              '&:hover': {
            backgroundColor: "#f4ece2",
            borderColor: "#d4af37"
            }
        }}
        >
        📧 {t("SendPasswordsByEmail")}
        </Button>

      </Box>
      <MainMenu headerHeight={headerHeight} isMobile={isMobile} />   
       
       <SnackbarFeedback
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        bottomNavHeight={bottomNavHeight}
      />
    </>  
  );
};

export default ConnexionGroupe;
